import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { markFirstStepDone } from '../../api/userOnboarding';
import { OnboardingBanner } from '../GettingStarted/OnboardingBanner';
import {
  FirstStepResult,
  guidanceFrom,
  homeFor,
  seasonOf,
  surveyTypeFor,
} from '../GettingStarted/gettingStartedView';
import { USER_ONBOARDING_KEY } from '../../hooks/queries/onboarding/useUserOnboarding';
import { useQueryClient } from '@tanstack/react-query';
import {
  SurveyFormCard,
  SurveyFormData,
} from '../../components/SurveyFormCard/SurveyFormCard';
import { useSelectedUnitContext } from '../../contexts/selectedUnitContext';
import { useEffect, useRef, useState } from 'react';
import { useGetTeamsQuery } from '../../hooks';
import { SurveyFormCardUtils } from '../../utils/SurveyFormCardUtils/SurveyFormCard.utils';
import { useCreateSurveyMutation } from '../../hooks/mutations/useCreateSurveyMutation';
import { useCreateWorkEnvironmentSurveyMutation } from '../../hooks/mutations/useCreateWorkEnvironmentSurveyMutation';
import { useCreateCustomSurveyMutation } from '../../hooks/mutations/useCreateCustomSurveyMutation';
import { Routes } from '../../enums/Routes';
import { RiskMatrixNotice } from './RiskMatrixNotice';

export type { SurveyFormData };

export const CreateSurveyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedTeamId } = useSelectedUnitContext();
  const { data: teams } = useGetTeamsQuery();
  const queryClient = useQueryClient();
  // The second step of «Kom i gang»: the new team, the type of the goal and a suggested name
  const [params] = useSearchParams();
  const guidance = guidanceFrom(params);
  const guidedTeam = Number(params.get('teamId'));
  const teamId = guidance && guidedTeam > 0 ? guidedTeam : selectedTeamId;

  // What was sent, so «Kom i gang» can say what was made
  const lastSubmitted = useRef<SurveyFormData | null>(null);
  // Re-enables the submit button when the request fails
  const resetSubmitButton = useRef<(() => void) | undefined>(undefined);

  const onSuccess = () => {
    setError(null);
    SurveyFormCardUtils.removeSessionKeys(false);
    if (!guidance) return navigate(Routes.CurrentSurveys);
    // Back to «Kom i gang», which says what was made and shows the next step
    const result: FirstStepResult = {
      title: lastSubmitted.current?.name ?? '',
      team: teams?.find((team) => team.id === lastSubmitted.current?.team)?.name ?? '',
      planned: Boolean(lastSubmitted.current?.isPlanning),
    };
    void markFirstStepDone(guidance.goal === 'WORK_ENVIRONMENT' ? 'workEnvironmentSurvey' : 'survey')
      .then((onboarding) => queryClient.setQueryData(USER_ONBOARDING_KEY, onboarding))
      .catch(() => queryClient.invalidateQueries({ queryKey: USER_ONBOARDING_KEY }))
      .finally(() => navigate(homeFor(guidance.goal) ?? Routes.CurrentSurveys, { state: { firstStep: result } }));
  };


  const onError = (error: Error) => {
    setError({ name: error.name, message: error.message });
    resetSubmitButton.current?.();
  };

  const { mutate: createSurveyMutation } = useCreateSurveyMutation(teamId, {
    onSuccess,
    onError,
  });
  const { mutate: createWorkEnvironmentSurveyMutation } =
    useCreateWorkEnvironmentSurveyMutation(teamId, {
      onSuccess,
      onError,
    });
  const { mutate: createCustomSurveyMutation } =
    useCreateCustomSurveyMutation(teamId, {
      onSuccess,
      onError,
    });

  const [formData, setFormData] = useState<SurveyFormData>(() => {
    const initial = SurveyFormCardUtils.getInitialFormData(teamId);
    if (!guidance) return initial;
    const now = new Date();
    return {
      ...initial,
      team: teamId,
      surveyType: surveyTypeFor(guidance.goal),
      name:
        initial.name ||
        t(`gettingStarted.banner.surveyNames.${guidance.goal}`, {
          season: t(`gettingStarted.banner.seasons.${seasonOf(now)}`),
          year: now.getFullYear(),
        }),
    };
  });
  const [error, setError] = useState<{ name: string; message: string } | null>(
    null,
  );

  const getInitialStep = (): number => {
    const stored = sessionStorage.getItem(SurveyFormCardUtils.SURVEY_STEP_KEY);
    return stored ? Number(stored) : 0;
  };

  const [currentStep, setCurrentStep] = useState<number>(getInitialStep);

  useEffect(() => {
    sessionStorage.setItem(
      SurveyFormCardUtils.SURVEY_DRAFT_KEY,
      JSON.stringify(formData),
    );
  }, [formData]);

  useEffect(() => {
    sessionStorage.setItem(
      SurveyFormCardUtils.SURVEY_STEP_KEY,
      String(currentStep),
    );
  }, [currentStep]);

  const onCreateSurvey = async (
    form: SurveyFormData,
    activeButton?: () => void,
  ) => {
    setError(null);
    resetSubmitButton.current = activeButton;
    lastSubmitted.current = form;

    const formDate: string =
      form.isPlanning && form.date ? form.date + 'T' + form.time : '';
    const scheduledDate =
      form.isPlanning && formDate ? new Date(formDate) : undefined;

    if (form.surveyType === 'custom') {
      if (!form.customTemplateId) {
        setError({
          name: t('surveyCreation.wizard.submitErrorTitle'),
          message: t('surveyCreation.validation.customTemplate'),
        });
        activeButton?.();
        return;
      }

      createCustomSurveyMutation({
        templateId: form.customTemplateId,
        title: form.name,
        description: form.name,
        deadlineInDays: form.deadlineInDays
          ? Number(form.deadlineInDays)
          : undefined,
        scheduledTime: scheduledDate,
        frequency: form.frequency,
      });
      return;
    }

    if (form.surveyType === 'work-environment') {
      if (!form.workEnvironmentTemplateId) {
        setError({
          name: t('surveyCreation.wizard.submitErrorTitle'),
          message: t('surveyCreation.validation.workEnvironmentTemplate'),
        });
        activeButton?.();
        return;
      }

      createWorkEnvironmentSurveyMutation({
        teamId: form.team,
        title: form.name,
        templateVersionId: form.workEnvironmentTemplateId,
        deadlineInDays: form.deadlineInDays
          ? Number(form.deadlineInDays)
          : undefined,
        daysBeforeDeadlineToNotify: form.daysBeforeDeadlineToNotify,
        actionProposalDeadlineInDays: form.actionProposalDeadlineInDays
          ? Number(form.actionProposalDeadlineInDays)
          : undefined,
        actionVotingDeadlineInDays: form.actionVotingDeadlineInDays
          ? Number(form.actionVotingDeadlineInDays)
          : undefined,
        scheduledTime: scheduledDate?.toISOString(),
        frequency: form.frequency || undefined,
      });
    } else {
      if (!form.psychologicalSafetyTemplateId) {
        setError({
          name: t('surveyCreation.wizard.submitErrorTitle'),
          message: t('surveyCreation.validation.psychologicalSafetyTemplate'),
        });
        activeButton?.();
        return;
      }

      createSurveyMutation({
        teamId: form.team,
        description: form.name,
        title: form.name,
        templateId: form.psychologicalSafetyTemplateId,
        deadlineInDays: form.deadlineInDays
          ? Number(form.deadlineInDays)
          : undefined,
        daysBeforeDeadlineToNotify: form.daysBeforeDeadlineToNotify,
        scheduledTime: scheduledDate,
        frequency: form.frequency,
      });
    }
  };

  return (
    <SurveyFormCard
      formData={formData}
      setFormData={setFormData}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      teams={teams || []}
      onCreateSurvey={onCreateSurvey}
      backTo={guidance ? (homeFor(guidance.goal) ?? Routes.CurrentSurveys) : Routes.CurrentSurveys}
      backLabel={guidance ? t('gettingStarted.banner.back') : t('createSurveyPage.backLabel')}
      submitError={error}
      notice={formData.surveyType === 'work-environment' ? <RiskMatrixNotice /> : undefined}
      guidance={
        guidance
          ? {
              preselectTemplate: true,
              banner: (step) => (
                <OnboardingBanner withBackLink={false} goal={guidance.goal} step={2} tip={t(`gettingStarted.banner.tips.${step}`)} />
              ),
            }
          : undefined
      }
    />
  );
};
